import { DateTime } from 'luxon'
import {
  CustomerProfileAuditField,
  CustomerSchedule,
  CustomerScheduleItem,
  CustomerSource,
  EMPTY_HISTORY,
  GetHistoryResponse,
  HistoryEntry,
  ManagedCustomer,
} from '@seiri/shared/common'
import { historyActorText, legacyStampShown } from './history'

const at = (value: Date | string, timezone: string) =>
  DateTime.fromJSDate(new Date(value)).setZone(timezone).setLocale('sk')

const fullName = (person?: { firstName?: string; lastName?: string } | null) =>
  person ? `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim() : ''

/** An age as Slovak says it: "1 rok", "3 roky", "34 rokov". */
export function yearsText(years: number): string {
  return `${years} ${years === 1 ? 'rok' : years >= 2 && years <= 4 ? 'roky' : 'rokov'}`
}

/** Where a client came from, as Údaje names it ("Zdroj"). */
export const CUSTOMER_SOURCE_LABELS: Record<CustomerSource, string> = {
  [CustomerSource.PublicReservation]: 'Verejná rezervácia',
  [CustomerSource.PublicEvent]: 'Prihlásenie na udalosť',
  [CustomerSource.Manual]: 'Pridaný ručne',
  [CustomerSource.ClientZone]: 'Klientska zóna',
}

/** "Verejná rezervácia · 3. 2. 2026", "Pridaný ručne · Eva Prvá", "Neznámy" for a client older than the field. */
export function clientSourceText(customer: ManagedCustomer, timezone: string): string {
  if (!customer.source) {
    return 'Neznámy'
  }
  const detail =
    customer.source === CustomerSource.Manual
      ? fullName(customer.createdBy)
      : at(customer.createdAt, timezone).toFormat('d. M. yyyy')
  return [CUSTOMER_SOURCE_LABELS[customer.source], detail].filter(Boolean).join(' · ')
}

/** "z verejnej rezervácie", as the status line and História say how a client was created. */
const CREATED_FROM: Record<CustomerSource, string> = {
  [CustomerSource.PublicReservation]: 'z verejnej rezervácie',
  [CustomerSource.PublicEvent]: 'z prihlásenia na udalosť',
  [CustomerSource.Manual]: 'ručne',
  [CustomerSource.ClientZone]: 'v klientskej zóne',
}

/** The next entry the client is expected at: a reservation that is not cancelled, or a confirmed sign-up. */
export function nextClientVisit(schedule: CustomerSchedule, now: Date): CustomerScheduleItem | null {
  return (
    schedule.items.find(
      (item) =>
        new Date(item.start) > now &&
        (item.kind === 'reservation' ? item.state !== 'CANCELLED' : item.status === 'confirmed'),
    ) ?? null
  )
}

/**
 * The line under Profil klienta (docs/clients.md, _Client detail page_): what the client's state means for staff.
 *
 * - **Blocked** says so first, because it is what stops the client booking on the web.
 * - **New** - nothing in the past yet - says where the client came from.
 * - Otherwise the next date and the last visit.
 */
export function clientStatusSentence(
  customer: ManagedCustomer,
  schedule: CustomerSchedule | null,
  now: Date,
  timezone: string,
): string {
  if (customer.blocked) {
    return 'Klient je zablokovaný: nemôže sa cez web prihlasovať na udalosti ani rezervovať si termíny. Pri zablokovaní dostal e-mail.'
  }
  const next = schedule ? nextClientVisit(schedule, now) : null
  const nextText = next
    ? `Najbližší termín ${at(next.start, timezone).toFormat("ccc d. M. 'o' HH:mm")}`
    : 'Žiadny nadchádzajúci termín'

  if (schedule && schedule.counts.past === 0) {
    const created = at(customer.createdAt, timezone).toFormat('d. M. yyyy')
    const origin = customer.source ? `vznikol ${CREATED_FROM[customer.source]} ${created}` : `v prevádzke od ${created}`
    return `Nový klient – ${origin}. ${nextText}.`
  }

  const lastVisit = schedule?.counts.lastVisit
  const lastText = lastVisit ? `, posledná návšteva ${at(lastVisit, timezone).toFormat('ccc d. M.')}` : ''
  // A date ends the sentence with its own full stop ("ut 29. 9.").
  const sentence = `Klient sa môže cez web prihlasovať na udalosti aj rezervovať si termíny. ${nextText}${lastText}`
  return sentence.endsWith('.') ? sentence : `${sentence}.`
}

/** One line of História klienta. */
export interface ClientHistoryItem {
  at: Date
  text: string
  detail: string
}

/** A client's profile fields as História klienta names them (docs/wording.md, _História_). */
export const CUSTOMER_PROFILE_FIELD_LABELS: Record<CustomerProfileAuditField, string> = {
  firstName: 'meno',
  lastName: 'priezvisko',
  phone: 'telefón',
  birthDate: 'dátum narodenia',
  gender: 'pohlavie',
  description: 'poznámka',
}

/**
 * One audit entry about the client as a História klienta item; null for one the card does not show. A card taken away
 * is already told from `pastCardIds`, and the client's reservations and sign-ups from their own pages.
 */
function clientAuditItem(entry: HistoryEntry, timezone: string): ClientHistoryItem | null {
  const happened = new Date(entry.at)
  const when = DateTime.fromJSDate(happened).setZone(timezone).toFormat("d. M. yyyy 'o' HH:mm")
  const detail = (...parts: (string | null | false)[]) => [when, ...parts].filter(Boolean).join(' · ')
  const by = historyActorText(entry.actor)
  switch (entry.kind) {
    case 'customer.blocked':
    case 'customer.unblocked':
      return {
        at: happened,
        text: entry.kind === 'customer.blocked' ? 'Klient zablokovaný' : 'Klient odblokovaný',
        detail: detail(by, !!entry.data.message && `správa: „${entry.data.message}“`),
      }
    case 'customer.cardChanged':
      return entry.data.to ? { at: happened, text: `Karta ${entry.data.to} pridelená`, detail: detail(by) } : null
    case 'customer.pointsChanged':
      return { at: happened, text: `Vernostné body ${entry.data.from} → ${entry.data.to}`, detail: detail(by) }
    case 'customer.edited':
      return {
        at: happened,
        text: 'Údaje upravené',
        detail: detail(by, entry.data.fields.map((field) => CUSTOMER_PROFILE_FIELD_LABELS[field]).join(', ')),
      }
    case 'customer.consentGranted':
      return {
        at: happened,
        text: 'Súhlas s marketingom udelený',
        detail: detail(entry.data.via === 'event' ? 'pri prihlásení na udalosť cez web' : 'pri rezervácii cez web'),
      }
    default:
      return null
  }
}

/**
 * What is known to have happened to the client record, newest first: its creation and source, each replaced e-mail
 * (the pairing moved with it) and each revoked card from the row, and from the audit log (docs/audit.md) each block
 * and unblock, card given, points change, profile edit and marketing consent. Before the client's first audit row the
 * last edit comes from its stamp (`legacyStampShown`).
 */
export function clientHistory(
  customer: ManagedCustomer,
  timezone: string,
  audit: GetHistoryResponse = EMPTY_HISTORY,
): ClientHistoryItem[] {
  const when = (value: Date | string) => at(value, timezone).toFormat("d. M. yyyy 'o' HH:mm")
  const day = (value: Date | string) => at(value, timezone).toFormat('d. M. yyyy')
  const created = new Date(customer.createdAt)
  const items: ClientHistoryItem[] = []

  const name = fullName(customer)
  switch (customer.source) {
    case CustomerSource.PublicReservation:
    case CustomerSource.PublicEvent:
      items.push({
        at: created,
        text: `Klient vytvorený ${CREATED_FROM[customer.source]}${name ? `: ${name}` : ''}`,
        detail: `${when(created)} · verejná stránka prevádzky`,
      })
      break
    case CustomerSource.Manual: {
      const by = fullName(customer.createdBy)
      items.push({ at: created, text: `Klient pridaný ručne${by ? `: ${by}` : ''}`, detail: when(created) })
      break
    }
    case CustomerSource.ClientZone:
      items.push({ at: created, text: 'Klient sa pridal sám v klientskej zóne', detail: when(created) })
      break
    default:
      items.push({ at: created, text: 'Klient vytvorený', detail: when(created) })
  }

  customer.pastEmails.forEach((past, index) => {
    const replacedBy = customer.pastEmails[index + 1]?.email ?? customer.email
    const replacedAt = new Date(past.replacedAt)
    items.push({
      at: replacedAt,
      text: replacedBy ? `E-mail zmenený z ${past.email} na ${replacedBy}` : `E-mail ${past.email} odstránený`,
      detail: `${day(replacedAt)} · párovanie sa presunulo s adresou`,
    })
  })

  for (const card of customer.pastCardIds) {
    const revokedAt = new Date(card.revokedAt)
    items.push({ at: revokedAt, text: `Karta ${card.cardId} odobraná`, detail: day(revokedAt) })
  }

  if (customer.updatedAt) {
    const updated = new Date(customer.updatedAt)
    if (updated.getTime() - created.getTime() > 60_000 && legacyStampShown(updated, customer.id, audit)) {
      const by = fullName(customer.updatedBy)
      items.push({ at: updated, text: `Naposledy upravený${by ? `: ${by}` : ''}`, detail: when(updated) })
    }
  }

  for (const entry of audit.entries) {
    const item = entry.subjectId === customer.id ? clientAuditItem(entry, timezone) : null
    if (item) {
      items.push(item)
    }
  }
  return items.sort((a, b) => b.at.getTime() - a.at.getTime())
}
